import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, type Category } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);

  useEffect(() => {
    const found = categories.find((c) => c.slug === slug) || categories[0];
    setActiveCategory(found);
  }, [slug]);

  if (!activeCategory) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <p className="text-foreground-600">加载中...</p>
      </div>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${activeCategory.name} - 禁漫天堂 JMComic 漫画分类`,
    description: activeCategory.description,
    url: `https://jmcomic-guide.com/category/${activeCategory.slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmcomic-guide.com/' },
      { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://jmcomic-guide.com/category/' },
      { '@type': 'ListItem', position: 3, name: activeCategory.name },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 禁漫天堂 JMComic 漫画分类浏览`}
        description={activeCategory.description}
        keywords={`禁漫天堂,${activeCategory.name},漫画分类,${activeCategory.name}漫画`}
        canonicalUrl={`https://jmcomic-guide.com/category/${activeCategory.slug}`}
      />
      <SchemaOrg schema={categorySchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {activeCategory.name}
            </h1>
            <p className="text-foreground-600 text-sm max-w-2xl">
              {activeCategory.description}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === activeCategory.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden mb-8">
            <div className="relative h-48 md:h-64">
              <img
                src={activeCategory.coverImage}
                alt={activeCategory.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500">
                    <i className={`${activeCategory.icon} text-background-50 text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-lg font-heading font-bold text-background-50">{activeCategory.name}</div>
                    <div className="text-sm text-background-200/80">收录 {activeCategory.count.toLocaleString()} 部漫画</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20with%20dynamic%20action%20pose%2C%20vibrant%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-01&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 1`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 1</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第120话</div>
              </div>
            </div>
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20romance%20theme%20with%20cherry%20blossoms%2C%20soft%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-02&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 2`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 2</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第135话</div>
              </div>
            </div>
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20mystery%20noir%20theme%2C%20dramatic%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-03&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 3`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 3</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第150话</div>
              </div>
            </div>
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20fantasy%20magic%20theme%2C%20whimsical%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-04&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 4`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 4</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第165话</div>
              </div>
            </div>
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20comedy%20slice%20of%20life%20theme%2C%20colorful%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-05&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 5`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 5</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第180话</div>
              </div>
            </div>
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20sci-fi%20futuristic%20theme%2C%20cyberpunk%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-06&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 6`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 6</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第195话</div>
              </div>
            </div>
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20horror%20dark%20theme%2C%20eerie%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-07&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 7`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 7</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第210话</div>
              </div>
            </div>
            <div className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-300/40 transition-all duration-200 cursor-pointer">
              <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20cover%20art%20martial%20arts%20wuxia%20theme%2C%20elegant%20anime%20illustration%20style%2C%20clean%20simple%20background%2C%20professional%20manga%20aesthetic&width=300&height=400&seq=cat-comic-08&orientation=portrait"
                  alt={`${activeCategory.name}漫画示例 8`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-foreground-900 line-clamp-1">{activeCategory.name}漫画示例 8</div>
                <div className="text-xs text-foreground-500 mt-1">更新至第225话</div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-foreground-500 mb-4">
              以上为{activeCategory.name}分类下的示例漫画，实际内容请访问禁漫天堂官网查看
            </p>
            <a
              href="https://jmcomic2.moe"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              访问官网浏览更多漫画
            </a>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}